import { createAction } from 'redux-starter-kit';
import { requestStart, requestFailed, requestSucceded } from '../../../domain/requests/RequestActions';
import {
  putChecklistItems, putChecklistItem, putChecklistItemReferences,
} from '../../../domain/checklistItems/checklistItemsActions';
import { get, post } from '../../../../utility/via-fetch';
import { addMessage } from '../../application/messageUiActions';


/**
 * An action that signifies a Checklist Item is being currently edited.
 * @example dispatch(editChecklistItem(checklistItemCode))
 */
export const editChecklistItemNew = createAction('EDIT_CHECKLIST_ITEM');

/**
 * An action that signifies no Checklist Item is being edited.
 * @example dispatch(cancelEditChecklistItem())
 */
export const cancelEditChecklistItem = createAction('CANCEL_EDIT_CHECKLIST_ITEM');

/**
 * An action that signifies that a fetch to add a Checklist Item has been
 * made in the Checklist Item Configuration screen. This should pass through the request id.
 * @example dispatch(fetchChecklistItem(requestId))
 */
export const fetchAddChecklistItem = createAction('FETCH_CREATE_CHECKLIST_ITEM');

/**
 * An action that signifies that a fetch to update a Checklist Item
 * has been made in the Checklist Item Configuration screen. This should pass through the request id.
 * @example dispatch(fetchUpdateChecklistItemId(requestId));
 */
export const fetchUpdateChecklistItem = createAction('FETCH_UPDATE_CHECKLIST_ITEM');

export const addPublishChecklistItem = createAction('ADD_PENDING_PUBLISH_CHECKLIST_ITEM');

export const removePublishChecklistItem = createAction('REMOVE_PENDING_PUBLISH_CHECKLIST_ITEM');

/**
 * An action that signifies a fetch request to load Checklist Items has been made.
 * @example dispatch(fetchChecklistItems(requestId))
 */
export const fetchChecklistItems = createAction('FETCH_CHECKLIST_ITEMS');

export function requestChecklistItems() {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchChecklistItems.toString()));
    dispatch(fetchChecklistItems(requestId));
    try {
      const checklistItems = await get('/api/checklist-items/');

      // Normalise data. Store the data in the domain, and the ids in the request response.
      dispatch(putChecklistItems(checklistItems));
      dispatch(requestSucceded(requestId, checklistItems.map(checklistItem => checklistItem.itemCode)));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * An action that signifies a fetch request has been made that loads reference areas of a Checklist Item.
 * @example dispatch(fetchChecklistItemReferences(requestId))
 */
export const fetchChecklistItemReferences = createAction('FETCH_CHECKLIST_ITEM_REFERENCES');

export function requestForChecklistItemReferences(checklistItem) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchChecklistItemReferences.toString()));
    dispatch(fetchChecklistItemReferences(requestId));
    try {
      const checklistItemReferences = await get(`/api/checklist-items/${checklistItem}/checklists/`);
      dispatch(putChecklistItemReferences(checklistItemReferences));
      dispatch(requestSucceded(requestId, checklistItemReferences.map(checklistItemRefs => checklistItemRefs.description)));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

export function requestPublishChecklistItem(checklistItem) {
  const toggledChecklistItem = { ...checklistItem, active: !checklistItem.active };

  return async (dispatch) => {
    const requestId = dispatch(requestStart(addPublishChecklistItem.toString()));
    dispatch(addPublishChecklistItem(toggledChecklistItem.itemCode));
    try {
      await post(`/api/checklist-items/${checklistItem.itemCode}`, {}, toggledChecklistItem);
      dispatch(putChecklistItem(toggledChecklistItem));
      dispatch(addMessage('success', 'itemStateUpdated', { item: checklistItem.itemDescription }));
      dispatch(requestSucceded(requestId));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
    dispatch(removePublishChecklistItem(toggledChecklistItem.itemCode));
  };
}

/**
 * An action for creating a Checklist Item on the server.
 * @param {object} checklistItem A Checklist Item to be created.
 * @example dispatch(requestChecklistItem(checklistItem));
 */
export function requestAddChecklistItem(checklistItem) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart(fetchAddChecklistItem.toString()));
    dispatch(fetchAddChecklistItem(requestId));
    try {
      // Construct our new type object
      const createdChecklistItem = {
        ...checklistItem,
      };

      // Create a new Checklist Item
      createdChecklistItem.itemCode = await post('/api/checklist-items', {}, checklistItem);
      dispatch(putChecklistItem(createdChecklistItem));
      dispatch(cancelEditChecklistItem());
      dispatch(requestSucceded(requestId, createdChecklistItem.itemCode));
      dispatch(addMessage('success', 'itemSaved', { item: createdChecklistItem.itemDescription }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}

/**
 * Updates the Checklist Item with new data.
 * @param {object} checklistItem The Checklist Item to be crreated.
 */
export function requestUpdateChecklistItem(checklistItem) {
  return async (dispatch) => {
    const requestId = dispatch(requestStart());
    dispatch(fetchUpdateChecklistItem(requestId));
    try {
      const updatedChecklistItem = {
        ...checklistItem,
      };

      await post(`/api/checklist-items/${checklistItem.itemCode}`, {}, updatedChecklistItem);
      dispatch(cancelEditChecklistItem());
      dispatch(putChecklistItem(updatedChecklistItem));
      dispatch(requestSucceded(requestId, updatedChecklistItem.itemCode));
      dispatch(addMessage('success', 'itemUpdated', { item: updatedChecklistItem.itemDescription }));
    } catch (error) {
      dispatch(requestFailed(requestId, error));
    }
  };
}
